/** Application roles stored in PostgreSQL enum + JWT app_metadata */
export const ROLES = {
  INVESTOR: 'investor',
  STAFF: 'staff',
  ADMIN: 'admin',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/** Deal lifecycle for syndication-style offerings */
export const DEAL_STATUSES = {
  DRAFT: 'draft',
  OPEN: 'open',
  CLOSED: 'closed',
  FUNDED: 'funded',
} as const;

export type DealStatus = (typeof DEAL_STATUSES)[keyof typeof DEAL_STATUSES];

/** Investment lifecycle including Stripe payment */
export const INVESTMENT_STATUSES = {
  PENDING_PAYMENT: 'pending_payment',
  ACTIVE: 'active',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

export type InvestmentStatus =
  (typeof INVESTMENT_STATUSES)[keyof typeof INVESTMENT_STATUSES];

export const TRANSACTION_TYPES = {
  INVESTMENT: 'investment',
  PAYOUT: 'payout',
} as const;

export type TransactionType =
  (typeof TRANSACTION_TYPES)[keyof typeof TRANSACTION_TYPES];

export const ACCREDITATION = {
  ACCREDITED: 'accredited',
  NON_ACCREDITED: 'non_accredited',
  UNKNOWN: 'unknown',
} as const;

export type AccreditationStatus =
  (typeof ACCREDITATION)[keyof typeof ACCREDITATION];

/** Storage bucket names (must match Supabase Storage config) */
export const STORAGE_BUCKETS = {
  DEAL_ASSETS: 'deal-assets',
  DEAL_DOCUMENTS: 'deal-documents',
  PROFILE_DOCUMENTS: 'profile-documents',
  AVATARS: 'avatars',
} as const;

/** CRM contact classification — investors/brokers/owners/leads */
export const CRM_CONTACT_TYPES = {
  INVESTOR: 'investor',
  BROKER: 'broker',
  OWNER: 'owner',
  LEAD: 'lead',
} as const;

export type CrmContactType =
  (typeof CRM_CONTACT_TYPES)[keyof typeof CRM_CONTACT_TYPES];

/**
 * Kanban + pipeline stages.
 * Matches admin board: New → Lost/Inactive (stored as `lost`).
 */
export const CRM_PIPELINE_STATUSES = {
  NEW: 'new',
  CONTACTED: 'contacted',
  ENGAGED: 'engaged',
  QUALIFIED: 'qualified',
  CONVERTED: 'converted',
  LOST: 'lost',
} as const;

export type CrmPipelineStatus =
  (typeof CRM_PIPELINE_STATUSES)[keyof typeof CRM_PIPELINE_STATUSES];

/** Activity timeline entries — communication + standalone notes */
export const CRM_ACTIVITY_TYPES = {
  CALL: 'call',
  EMAIL: 'email',
  MEETING: 'meeting',
  NOTE: 'note',
} as const;

export type CrmActivityType =
  (typeof CRM_ACTIVITY_TYPES)[keyof typeof CRM_ACTIVITY_TYPES];

/** Marketing / sourcing attribution for leads */
export const CRM_LEAD_SOURCES = {
  WEBSITE: 'website',
  REFERRAL: 'referral',
  COLD_OUTREACH: 'cold_outreach',
  OTHER: 'other',
} as const;

export type CrmLeadSource =
  (typeof CRM_LEAD_SOURCES)[keyof typeof CRM_LEAD_SOURCES];
